import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { loadScriptByURL, SITE_KEY, validationErrorsToString } from '../../utils';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';

// ICONS
import { ReactComponent as TickIcon } from '../../assets/svg/tick-circle.svg';

// TYPES
import { AuthenticationForgotPasswordCommand, AccountRegistrationCommand } from '../../api/models';

interface ForgotFormTypes {
	organisationIdentifier?: AccountRegistrationCommand['organisationIdentifier'];
	paymentPlanId?: AccountRegistrationCommand['paymentPlanId'];
	profileType?: AccountRegistrationCommand['profileType'];
}

export const ForgotForm: React.FC<ForgotFormTypes> = ({ organisationIdentifier, paymentPlanId, profileType }) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const [emailSent, setEmailSent] = React.useState<boolean>(false);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const formRef = React.createRef<HTMLFormElement>();

	React.useEffect(() => {
		if (formRef.current) {
			formRef.current.addEventListener('click', setFormErrorMessage.bind(null, ''));
		}

		return () => {
			if (formRef.current) {
				formRef.current.removeEventListener('click', setFormErrorMessage.bind(null, ''));
			}
		};
	}, []);

	const initialValues: AuthenticationForgotPasswordCommand = {
		email: ''
	};

	const validationSchema = Yup.object({
		email: Yup.string().required('Required')
	});

	React.useEffect(() => {
		// load google reCAPTCHA script
		loadScriptByURL('recaptcha-key', function () {});
	}, []);

	const doLogin = async (values: AuthenticationForgotPasswordCommand) => {
		setLoading(true);

		await window.grecaptcha.ready(() => {
			window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				login(values, token);
			});
		});
	};

	const login = async (values: AuthenticationForgotPasswordCommand, token: string) => {
		const response = await apibridge.doForgotPassword({
			email: values.email,
			captchaToken: token
		});

		setLoading(false);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
			);
		} else if (response.result) {
			setEmailSent(true);
		}
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={doLogin}>
			{(formik) => {
				return (
					<Form translate="yes" ref={formRef}>
						<div className="content">
							<div className="form-group">
								<label htmlFor="email">Email</label>

								<Field name="email" id="email" type="text" className="form-control" />

								<ErrorMessage name="email" component="div" className="form-validation-error" />
							</div>
						</div>

						<div className="actions">
							{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}
							
							{!!emailSent && (
								<div className="form-validation-success">
									<TickIcon className="icon" />
									Email sent
								</div>
							)}

							<Button type="submit" className="btn-primary full-width" disabled={loading}>
								Send email
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default ForgotForm;
